// ./src/components/features/Budget/budgetHelpers.ts
//
// Display helpers for the event budget feature: calc-type labels / icons / hints,
// payment-method labels / icons, the polymorphic party icon, and a due-date
// formatter. Mirrors taskHelpers.

import { BUDGET_CALC_TYPE, BUDGET_PAYMENT_METHOD, BUDGET_PARTY_KIND, BUDGET_SYSTEM_CALC_TYPE } from "@/utils/api";

export interface CalcTypeMeta {
  value: string;
  label: string;
  icon: string;
  /** One-line explanation of how the amount is derived. */
  hint: string;
}

// Ordered for the picker.
export const CALC_TYPE_META: CalcTypeMeta[] = [
  {
    value: BUDGET_CALC_TYPE.FIXED,
    label: "Fixed amount",
    icon: "Coin",
    hint: "A flat amount you enter.",
  },
  {
    value: BUDGET_CALC_TYPE.PRICE_QUANTITY,
    label: "Price × quantity",
    icon: "Calculator",
    hint: "A unit price times a quantity you set.",
  },
  {
    value: BUDGET_CALC_TYPE.PER_ATTENDEE,
    label: "Per attendee (headcount)",
    icon: "Users",
    hint: "Unit price × everyone counted as attending (any RSVP).",
  },
  {
    value: BUDGET_CALC_TYPE.PER_ATTENDING,
    label: "Per attending guest",
    icon: "UserCheck",
    hint: "Unit price × guests who are attending (RSVP approved).",
  },
  {
    value: BUDGET_CALC_TYPE.PER_ARRIVED,
    label: "Per arrived guest",
    icon: "DoorEnter",
    hint: "Unit price × guests who have checked in.",
  },
  {
    value: BUDGET_CALC_TYPE.BY_RSVP_GROUP,
    label: "Per guest in RSVP group(s)",
    icon: "UsersGroup",
    hint: "Unit price × guests in the RSVP group(s) you pick.",
  },
];

// #A4.7 — calc types the APP sets; described for the list, never offered in the picker (CALC_TYPE_META).
export const SYSTEM_CALC_TYPE_META: CalcTypeMeta[] = [
  {
    value: BUDGET_SYSTEM_CALC_TYPE.STAFF_PAY,
    label: "Staff pay (kept by the app)",
    icon: "Cash",
    hint: "Paid volunteers: expected pay (shifts ahead and open places) plus what was worked.",
  },
  {
    value: BUDGET_SYSTEM_CALC_TYPE.ARTIST_DEAL,
    label: "Artist's door deal (kept by the app)",
    icon: "Ticket",
    hint: "What the artist's deal pays on the door so far — the act's share of admission sales, or its guarantee if that is more. Change the deal on the booking.",
  },
];

const CALC_TYPE_BY_VALUE: Record<string, CalcTypeMeta> = [...CALC_TYPE_META, ...SYSTEM_CALC_TYPE_META].reduce(
  (acc, m) => ({ ...acc, [m.value]: m }),
  {}
);

export const getCalcTypeMeta = (calcType?: string | null): CalcTypeMeta | null =>
  calcType ? CALC_TYPE_BY_VALUE[calcType] ?? null : null;

export const getCalcTypeLabel = (calcType?: string | null): string =>
  getCalcTypeMeta(calcType)?.label ?? "Fixed amount";

/** Whether a calc type multiplies a unit price by a live guest headcount. */
export const isPerHeadCalcType = (calcType?: string | null): boolean =>
  calcType === BUDGET_CALC_TYPE.PER_ATTENDEE ||
  calcType === BUDGET_CALC_TYPE.PER_ATTENDING ||
  calcType === BUDGET_CALC_TYPE.PER_ARRIVED ||
  calcType === BUDGET_CALC_TYPE.BY_RSVP_GROUP;

// ── Payment method ──

export interface PaymentMethodMeta {
  value: string;
  label: string;
  icon: string;
}

export const PAYMENT_METHOD_META: PaymentMethodMeta[] = [
  { value: BUDGET_PAYMENT_METHOD.CASH, label: "Cash", icon: "Cash" },
  { value: BUDGET_PAYMENT_METHOD.CARD, label: "Card", icon: "CreditCard" },
  { value: BUDGET_PAYMENT_METHOD.BANK_TRANSFER, label: "Bank transfer", icon: "BuildingBank" },
  { value: BUDGET_PAYMENT_METHOD.CHEQUE, label: "Cheque", icon: "FileText" },
  { value: BUDGET_PAYMENT_METHOD.OTHER, label: "Other", icon: "DotsCircleHorizontal" },
];

const PAYMENT_METHOD_BY_VALUE: Record<string, PaymentMethodMeta> = PAYMENT_METHOD_META.reduce(
  (acc, m) => ({ ...acc, [m.value]: m }),
  {}
);

export const getPaymentMethodLabel = (method?: string | null): string =>
  method ? PAYMENT_METHOD_BY_VALUE[method]?.label ?? method : "—";

export const getPaymentMethodIcon = (method?: string | null): string =>
  (method ? PAYMENT_METHOD_BY_VALUE[method]?.icon : undefined) ?? "Cash";

// ── Party (responsible / paid-by) ──

const PARTY_KIND_ICON: Record<string, string> = {
  [BUDGET_PARTY_KIND.USER]: "UserCheck",
  [BUDGET_PARTY_KIND.CREW]: "Users",
  [BUDGET_PARTY_KIND.GUEST]: "User",
  [BUDGET_PARTY_KIND.VENDOR]: "Briefcase",
  [BUDGET_PARTY_KIND.VENUE]: "MapPin",
};

export const getPartyKindIcon = (kind?: string | null): string =>
  (kind ? PARTY_KIND_ICON[kind] : undefined) ?? "User";

// ── Due date ──

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-07-01" → "Jul 1, 2026". Parses the date parts directly (no timezone shift). */
export const formatDueDate = (dueDate?: string | null): string | null => {
  if (!dueDate) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueDate);
  if (!match) {
    return dueDate;
  }

  const [, year, month, day] = match;
  const monthName = MONTHS[Number(month) - 1] ?? month;

  return `${monthName} ${Number(day)}, ${year}`;
};
